/**
 * Delay codes and fuel uplift live on the trip. Post-flight, the journey
 * log, and ACARS all read these rows. Nothing here opens a second record.
 *
 * Fuel is stored in whole pounds. Volume uses the desk Jet A density
 * (`JET_A_LB_PER_US_GAL` in fuelUnits.ts, 6.7 lb per US gallon).
 */
import { delayCodeLabel, delayShortReason, lookupDelayCode, normalizeReportedDelayCode } from "./delayCodes";
import { convertFuel } from "./fuelUnits";
import type {
  AcarsMsg,
  DelayRecord,
  FuelUpliftRecord,
  Mission,
  TripOpsSource,
  TripOpsStatus,
} from "./types";

export const DELAY_MINUTES_MAX = 24 * 60;
export const FUEL_UPLIFT_LB_MAX = 200_000;

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"] as const;

export function wholePounds(amount: number, unit: "lb" | "kg" | "L" | "USG"): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const from = unit === "USG" ? "gal" : unit;
  const pounds = Math.round(convertFuel(amount, from, "lb", "jeta"));
  if (!Number.isFinite(pounds) || pounds < 1 || pounds > FUEL_UPLIFT_LB_MAX) return null;
  return pounds;
}

export function parseDelayMinutes(raw: unknown): number | null {
  const n = typeof raw === "number" ? raw : Number(String(raw ?? "").trim());
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(DELAY_MINUTES_MAX, Math.round(n));
}

function icao(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 4);
}

function cleanRemark(raw: unknown): string {
  return String(raw ?? "")
    .replace(/[\u0000-\u001f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);
}

export function opsRowId(prefix: string, now: Date, salt = ""): string {
  const extra = salt ? `-${salt.replace(/[^a-z0-9]/gi, "").slice(0, 12)}` : "";
  return `${prefix}-${now.getTime().toString(36)}${extra}`;
}

export function hhmmZ(iso: string | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}${mm}Z`;
}

export function utcDateToken(iso: string | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  const d = Number.isFinite(t) ? new Date(t) : new Date();
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${dd}${MONTHS[d.getUTCMonth()]}${String(d.getUTCFullYear()).slice(2)}`;
}

/** Combine a `HHMM` ETD with the trip's STD date. Rolls to the next UTC day when the clock is earlier. */
export function etdIsoFromHhmm(stdIso: string | undefined, hhmm: string, now = new Date()): string | null {
  const token = hhmm.trim().toUpperCase().replace(/Z$/, "");
  if (!/^\d{4}$/.test(token)) return null;
  const hh = Number(token.slice(0, 2));
  const mm = Number(token.slice(2));
  if (hh > 23 || mm > 59) return null;
  const baseMs = Date.parse(stdIso ?? "") ;
  const base = Number.isFinite(baseMs) ? new Date(baseMs) : now;
  const etd = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(), hh, mm, 0, 0));
  if (Number.isFinite(baseMs) && etd.getTime() + 60_000 < baseMs) {
    etd.setUTCDate(etd.getUTCDate() + 1);
  }
  return etd.toISOString();
}

export function hydrateDelayRecord(raw: unknown): DelayRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<DelayRecord>;
  const id = String(row.id ?? "").trim().slice(0, 80);
  const minutes = parseDelayMinutes(row.minutes);
  if (!id || minutes == null) return null;
  const normalized = normalizeReportedDelayCode(row.code);
  const code = lookupDelayCode(row.code) ? lookupDelayCode(row.code)!.code : normalized.code;
  const rawCode = code === "99" ? cleanRemark(row.rawCode || normalized.rawCode).slice(0, 8) : undefined;
  const status: TripOpsStatus = row.status === "confirmed" ? "confirmed" : "pending";
  const source: TripOpsSource = row.source === "acars" || row.source === "companion" ? row.source : "desk";
  const etd = typeof row.etdUtc === "string" && Date.parse(row.etdUtc) ? row.etdUtc : undefined;
  return {
    id,
    code,
    minutes,
    ...(etd ? { etdUtc: new Date(etd).toISOString() } : {}),
    remarks: cleanRemark(row.remarks),
    ...(rawCode ? { rawCode } : {}),
    source,
    status,
    atUtc: typeof row.atUtc === "string" && Date.parse(row.atUtc) ? new Date(row.atUtc).toISOString() : new Date(0).toISOString(),
    ...(row.acarsMsgId ? { acarsMsgId: String(row.acarsMsgId).slice(0, 80) } : {}),
  };
}

export function hydrateDelayRecords(raw: unknown): DelayRecord[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: DelayRecord[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const next = hydrateDelayRecord(row);
    if (!next || seen.has(next.id)) continue;
    seen.add(next.id);
    out.push(next);
  }
  return out.length ? out : undefined;
}

export function hydrateFuelUplift(raw: unknown): FuelUpliftRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<FuelUpliftRecord>;
  const id = String(row.id ?? "").trim().slice(0, 80);
  const pounds = typeof row.pounds === "number" ? Math.round(row.pounds) : Number(row.pounds);
  const airportIcao = icao(row.airportIcao);
  if (!id || !Number.isFinite(pounds) || pounds < 1 || pounds > FUEL_UPLIFT_LB_MAX || airportIcao.length !== 4) return null;
  const status: TripOpsStatus = row.status === "confirmed" ? "confirmed" : "pending";
  const source: TripOpsSource = row.source === "acars" || row.source === "companion" ? row.source : "desk";
  return {
    id,
    pounds,
    airportIcao,
    ...(row.reported ? { reported: cleanRemark(row.reported).slice(0, 24) } : {}),
    source,
    status,
    atUtc: typeof row.atUtc === "string" && Date.parse(row.atUtc) ? new Date(row.atUtc).toISOString() : new Date(0).toISOString(),
    ...(row.acarsMsgId ? { acarsMsgId: String(row.acarsMsgId).slice(0, 80) } : {}),
  };
}

export function hydrateFuelUplifts(raw: unknown): FuelUpliftRecord[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: FuelUpliftRecord[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const next = hydrateFuelUplift(row);
    if (!next || seen.has(next.id)) continue;
    seen.add(next.id);
    out.push(next);
  }
  return out.length ? out : undefined;
}

export type FileDelayInput = {
  code: string;
  minutes: number;
  etdUtc?: string;
  hhmm?: string;
  remarks?: string;
  source?: TripOpsSource;
  status?: TripOpsStatus;
  acarsMsgId?: string;
  id?: string;
};

export function fileDelay(mission: Mission, input: FileDelayInput, now = new Date()): { ok: true; mission: Mission; record: DelayRecord } | { ok: false; error: string } {
  const minutes = parseDelayMinutes(input.minutes);
  if (minutes == null) return { ok: false, error: "Delay minutes must be a whole number from 1 to 1440." };
  const normalized = normalizeReportedDelayCode(input.code);
  const etdUtc = input.etdUtc && Date.parse(input.etdUtc) ? new Date(input.etdUtc).toISOString() : etdIsoFromHhmm(mission.departureIso, input.hhmm ?? "", now) ?? undefined;
  const known = lookupDelayCode(normalized.code);
  const remarks = [cleanRemark(input.remarks), normalized.rawCode ? `REPORTED ${normalized.rawCode}` : ""].filter(Boolean).join(" ").slice(0, 180);
  const record: DelayRecord = {
    id: input.id?.trim() || opsRowId("dly", now, normalized.code),
    code: known?.code ?? "99",
    minutes,
    ...(etdUtc ? { etdUtc } : {}),
    remarks,
    ...(normalized.rawCode && !known ? { rawCode: normalized.rawCode } : {}),
    source: input.source ?? "desk",
    status: input.status ?? (input.source && input.source !== "desk" ? "pending" : "confirmed"),
    atUtc: now.toISOString(),
    ...(input.acarsMsgId ? { acarsMsgId: input.acarsMsgId } : {}),
  };
  const delays = [...(hydrateDelayRecords(mission.delays) ?? []), record];
  return { ok: true, mission: { ...mission, delays }, record };
}

export function confirmDelay(mission: Mission, delayId: string): { ok: true; mission: Mission; record: DelayRecord } | { ok: false; error: string } {
  const delays = hydrateDelayRecords(mission.delays) ?? [];
  const hit = delays.find((row) => row.id === delayId);
  if (!hit) return { ok: false, error: "That delay is not on this trip." };
  const record: DelayRecord = { ...hit, status: "confirmed" };
  const nextDelays = delays.map((row) => (row.id === delayId ? record : row));
  let next: Mission = { ...mission, delays: nextDelays };
  if (record.etdUtc) next = { ...next, departureIso: record.etdUtc };
  return { ok: true, mission: next, record };
}

export function dismissDelay(mission: Mission, delayId: string): Mission {
  const delays = (hydrateDelayRecords(mission.delays) ?? []).filter((row) => row.id !== delayId || row.status === "confirmed");
  return { ...mission, delays: delays.length ? delays : undefined };
}

export type FileFuelInput = {
  pounds?: number;
  amount?: number;
  unit?: "lb" | "kg" | "L" | "USG";
  airportIcao: string;
  reported?: string;
  source?: TripOpsSource;
  status?: TripOpsStatus;
  acarsMsgId?: string;
  id?: string;
};

export function fileFuelUplift(mission: Mission, input: FileFuelInput, now = new Date()): { ok: true; mission: Mission; record: FuelUpliftRecord } | { ok: false; error: string } {
  const airportIcao = icao(input.airportIcao);
  if (airportIcao.length !== 4) return { ok: false, error: "Fuel uplift needs a 4-letter airport." };
  const pounds = input.pounds != null ? wholePounds(input.pounds, "lb") : wholePounds(input.amount ?? NaN, input.unit ?? "lb");
  if (pounds == null) return { ok: false, error: "Fuel uplift must be a whole number of pounds." };
  const record: FuelUpliftRecord = {
    id: input.id?.trim() || opsRowId("upl", now, airportIcao),
    pounds,
    airportIcao,
    ...(input.reported ? { reported: cleanRemark(input.reported).slice(0, 24) } : {}),
    source: input.source ?? "desk",
    status: input.status ?? (input.source && input.source !== "desk" ? "pending" : "confirmed"),
    atUtc: now.toISOString(),
    ...(input.acarsMsgId ? { acarsMsgId: input.acarsMsgId } : {}),
  };
  const fuelUplifts = [...(hydrateFuelUplifts(mission.fuelUplifts) ?? []), record];
  return { ok: true, mission: { ...mission, fuelUplifts }, record };
}

export function confirmFuelUplift(mission: Mission, upliftId: string): { ok: true; mission: Mission; record: FuelUpliftRecord } | { ok: false; error: string } {
  const rows = hydrateFuelUplifts(mission.fuelUplifts) ?? [];
  const hit = rows.find((row) => row.id === upliftId);
  if (!hit) return { ok: false, error: "That fuel uplift is not on this trip." };
  const record: FuelUpliftRecord = { ...hit, status: "confirmed" };
  return { ok: true, mission: { ...mission, fuelUplifts: rows.map((row) => (row.id === upliftId ? record : row)) }, record };
}

export function dismissFuelUplift(mission: Mission, upliftId: string): Mission {
  const rows = (hydrateFuelUplifts(mission.fuelUplifts) ?? []).filter((row) => row.id !== upliftId || row.status === "confirmed");
  return { ...mission, fuelUplifts: rows.length ? rows : undefined };
}

function rule(char = "-"): string {
  return char.repeat(68);
}

function line(label: string, value: string): string {
  return `${label.padEnd(8, " ")}${value}`;
}

/**
 * Airline-OFP style journey log. Monospace, header block, ruled sections, UTC.
 * Confirmed rows are the record. Pending rows stay visible so a downlink
 * is not dropped, and they are marked pending.
 */
export function renderJourneyLogText(input: {
  operator: string;
  flight: string;
  origin: string;
  dest: string;
  departureIso?: string;
  registration?: string;
  icaoType?: string;
  delays?: DelayRecord[] | null;
  fuelUplifts?: FuelUpliftRecord[] | null;
}): string {
  const delays = hydrateDelayRecords(input.delays) ?? [];
  const fuels = hydrateFuelUplifts(input.fuelUplifts) ?? [];
  const std = hhmmZ(input.departureIso) || "----Z";
  const etd = hhmmZ(delays.find((row) => row.status === "confirmed" && row.etdUtc)?.etdUtc) || std;
  const header = [
    rule("="),
    "NORTHSTAR DISPATCH".padEnd(34, " ") + "JOURNEY LOG",
    rule("="),
    `${line("FLT", (input.flight || "----").toUpperCase())}  ${line("DATE", utcDateToken(input.departureIso))}  UTC`,
    `${line("FROM", (input.origin || "----").toUpperCase())}  ${line("TO", (input.dest || "----").toUpperCase())}`,
    `${line("STD", std)}  ${line("ETD", etd)}`,
    `${line("REG", (input.registration || "----").toUpperCase())}  ${line("TYPE", (input.icaoType || "----").toUpperCase())}`,
    `${line("OPR", (input.operator || "OPERATOR").toUpperCase().slice(0, 40))}`,
    rule("-"),
    "DELAYS",
    "CODE  MIN   ETD    STATUS     REMARKS",
    rule("-"),
  ];
  const delayLines = delays.length
    ? delays.map((row) => {
        const label = delayCodeLabel(row.code);
        const remark = [row.remarks, label].filter(Boolean).join("  ").slice(0, 42);
        return `${row.code.padEnd(6, " ")}${String(row.minutes).padEnd(6, " ")}${(hhmmZ(row.etdUtc) || "----Z").padEnd(7, " ")}${row.status.toUpperCase().padEnd(11, " ")}${remark}`;
      })
    : ["NIL"];
  const fuelHeader = [rule("-"), "FUEL UPLIFT  LB", "APT     LB      STATUS     REPORTED", rule("-")];
  const fuelLines = fuels.length
    ? fuels.map((row) => `${row.airportIcao.padEnd(8, " ")}${String(row.pounds).padEnd(8, " ")}${row.status.toUpperCase().padEnd(11, " ")}${row.reported ?? ""}`)
    : ["NIL"];
  return [...header, ...delayLines, ...fuelHeader, ...fuelLines, rule("="), "END OF JOURNEY LOG  ALL TIMES UTC"].join("\n");
}

export function delayTelexFields(record: DelayRecord, mission: Pick<Mission, "originIcao" | "destIcao" | "departureIso">): Record<string, string> {
  return {
    code: record.code,
    minutes: String(record.minutes),
    etd: (hhmmZ(record.etdUtc) || hhmmZ(mission.departureIso)).replace(/Z$/, ""),
    origin: mission.originIcao,
    dest: mission.destIcao,
    short: delayShortReason(record.code),
  };
}

export function confirmedDelays(mission: Pick<Mission, "delays">): DelayRecord[] {
  return (hydrateDelayRecords(mission.delays) ?? []).filter((row) => row.status === "confirmed");
}

export function pendingDelays(mission: Pick<Mission, "delays">): DelayRecord[] {
  return (hydrateDelayRecords(mission.delays) ?? []).filter((row) => row.status === "pending");
}

export function confirmedUplifts(mission: Pick<Mission, "fuelUplifts">): FuelUpliftRecord[] {
  return (hydrateFuelUplifts(mission.fuelUplifts) ?? []).filter((row) => row.status === "confirmed");
}

export function projectTripOps<T extends { delays?: DelayRecord[]; fuelUplifts?: FuelUpliftRecord[]; acarsLog?: AcarsMsg[] }>(
  mission: Pick<Mission, "delays" | "fuelUplifts" | "acarsLog">,
): Pick<T, "delays" | "fuelUplifts" | "acarsLog"> {
  return {
    delays: hydrateDelayRecords(mission.delays),
    fuelUplifts: hydrateFuelUplifts(mission.fuelUplifts),
    acarsLog: Array.isArray(mission.acarsLog) ? mission.acarsLog : undefined,
  };
}
